import {
  Assembly,
  EquipmentCardLocationEnum,
  EquipmentCardPositionEnum,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useRollupOnData } from '@/hooks';
import { getTemplateFieldData } from '@/utils';
import { ComponentCardTemplateRenderer } from '../ComponentCard/ComponentCardFieldRenderer';

export const useComponentCardData = (component: Partial<Assembly>) => {
  const [selectedComponentId, setSelectedComponentId] = useAtom(state.selectedAssemblyId);
  const [selectedComponentIds, setSelectedComponentIds] = useAtom(state.selectedAssemblyIds);
  const setSelectedAssemblyName = useSetAtom(state.selectedAssemblyName);
  const setHoveredComponentId = useSetAtom(state.hoveredAssemblyId);
  const selectedViewerComponentId = useAtomValue(state.selectedAssemblyInfo);
  const selectedTemplate = useAtomValue(state.assemblyTemplate);

  const isSelectedComponent = useMemo(
    () => selectedViewerComponentId === component?.id,
    [selectedViewerComponentId, component?.id]
  );

  const handleCardEnter = useCallback(() => {
    setHoveredComponentId(component?.id ?? '');
  }, [component?.id, setHoveredComponentId]);

  const handleCardLeave = useCallback(() => {
    setHoveredComponentId(undefined);
  }, [setHoveredComponentId]);

  const handleChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setSelectedComponentIds((prev) => {
      if (event.target.checked) {
        return [...prev, component?.id ?? ''];
      } else {
        return prev.filter((id) => id !== component?.id);
      }
    });
  };

  const handleComponentCardClick = useCallback(
    (componentId: string, componentTagName: string) => {
      // Check if component is undefined or an empty object
      if (!component || Object.keys(component).length === 0) return;

      if (componentId !== selectedComponentId) {
        setSelectedComponentId(componentId);
        setSelectedAssemblyName(componentTagName);
      }
    },
    [component, selectedComponentId, setSelectedComponentId, setSelectedAssemblyName]
  );

  const isChecked = useMemo(() => {
    return selectedComponentIds.includes(component?.id ?? '');
  }, [selectedComponentIds, component?.id]);

  const fieldsData = getTemplateFieldData(
    selectedTemplate?.equipmentCard?.filter(
      (card) => card.location === EquipmentCardLocationEnum.MiViewer
    ) || [],
    component?.templateFieldData
  );
  const dataForPosition = useRollupOnData(fieldsData, 'position');

  const subtitle = (
    <ComponentCardTemplateRenderer
      data={dataForPosition[EquipmentCardPositionEnum.Subtitle]}
      isHighlighted={isSelectedComponent}
    />
  );

  const topMiddle = (
    <ComponentCardTemplateRenderer
      data={dataForPosition[EquipmentCardPositionEnum.TopMiddle]}
      isHighlighted={isSelectedComponent}
    />
  );
  const bottomMiddle = (
    <ComponentCardTemplateRenderer
      data={dataForPosition[EquipmentCardPositionEnum.BottomMiddle]}
      isHighlighted={isSelectedComponent}
    />
  );
  const topRight = (
    <ComponentCardTemplateRenderer
      data={dataForPosition[EquipmentCardPositionEnum.TopRight]}
      isHighlighted={isSelectedComponent}
    />
  );
  const bottomRight = (
    <ComponentCardTemplateRenderer
      data={dataForPosition[EquipmentCardPositionEnum.BottomRight]}
      isHighlighted={isSelectedComponent}
    />
  );

  return {
    isSelectedComponent,
    handleCardEnter,
    handleCardLeave,
    handleChecked,
    handleComponentCardClick,
    isChecked,
    subtitle,
    topMiddle,
    bottomMiddle,
    topRight,
    bottomRight,
  };
};
